import React, { useEffect, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

// Store Imports
import useFirmSelectionStore, { useFirmSelectionActions, useFirmSelectionState } from '../../../../../store/firmSelectionStore'

// MUI Imports
import Typography from '@mui/material/Typography'
import { Box, Checkbox, Tooltip } from '@mui/material'

// Third-party Imports
import classnames from 'classnames'
import { createColumnHelper, flexRender, getCoreRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues, getFilteredRowModel, useReactTable } from '@tanstack/react-table'

// Component Imports
import { TablePaginationComponentServerSide } from '../molecules/TablePaginationComponent'
import Flag from '../atoms/Flag'
import LoadingOverlay from '../atoms/LoadingOverlay'

// Utils Imports
import { countryEN } from '../../../../../assets/data/country-en'
import { countryTR } from '../../../../../assets/data/country-tr'

// Style Imports
import tableStyles from '../../styles/table.module.scss'

const httpPrefixer = (url) => {
  return (url?.indexOf('://') === -1) ? `http://${url}` : url
}

const getCountryName = (countryCode, language = 'en') => {
  const countryData = language === 'tr' ? countryTR : countryEN
  const country = countryData.find(c => c.countryCode === countryCode)
  return country ? country.countryName : countryCode
}

// Column Definitions
const columnHelper = createColumnHelper()

const FirmListServerSide = (props) => {
  const {
    firmKey,
    serverSidePagination,
    setServerSidePaginationByKey,
    setSelectedFirms,
    data,
    rowCount,
    fetching,
    languageGSD,
    className,
    disableInteraction = false
  } = props
  const { t } = useTranslation()

  const { selectionData } = useFirmSelectionState(firmKey)

  const { handleRowToggle } = useFirmSelectionActions()

  const isRowSelected = useFirmSelectionStore((state) => state.isRowSelected)

  useEffect(() => {
    if (setSelectedFirms) {
      setSelectedFirms(selectionData)
    }
  }, [selectionData, setSelectedFirms])

  const columns = useMemo(
    () => {
      const columnsArr = [
        columnHelper.display({
          id: 'select',
          header: () => null,
          cell: ({ row }) => (
            <Checkbox
              checked={isRowSelected(firmKey, row.original)}
              onChange={(e) => {
                e.stopPropagation()
                handleRowToggle(firmKey, row.original, !isRowSelected(firmKey, row.original))
              }}
              disabled={disableInteraction}
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          ),
          size: 50
        }),
        columnHelper.accessor('companyName', {
          header: t('mailTemplate.wizard.firmList.firmName'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.companyName}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('website', {
          header: t('mailTemplate.wizard.firmList.website'),
          meta: { flexGrow: 1 },
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col" onClick={(e) => {
                e.stopPropagation()
              }}>
                <a href={httpPrefixer(row.original.website)} target="_blank"
                   rel="noreferrer">
                  <Typography className="font-medium" style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {row.original.website}
                  </Typography>
                </a>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('emailList', {
          header: t('mailTemplate.wizard.firmList.email'),
          meta: { flexGrow: 1 },
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.emailList?.length > 1
                    ? <Tooltip
                      title={
                        <div style={{ whiteSpace: 'pre-line' }}>
                          {row.original.emailList.slice(0,
                            row.original.emailList.length).join('\n')}
                        </div>
                      }
                      arrow
                    >
                      <span style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block'
                      }}>
                        {row.original.emailList?.[0]}
                      </span>
                    </Tooltip>
                    : <span style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block'
                    }}>
                      {row.original.emailList?.[0]}
                    </span>
                  }
                </Typography>
              </div>
            </div>
          )
        }),
        ...(firmKey !== 'exporterFirms' && firmKey !== 'importerFirms'
          ? [
              columnHelper.accessor('import.countries', {
                header: t('mailTemplate.wizard.firmList.importCountries'),
                cell: ({ row }) => {
                  const countries = row.original.import?.countries || []
                  const maxVisible = 7

                  if (countries.length === 0) {
                    return <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
                  }

                  const visibleCountries = countries.slice(0, maxVisible)
                  const remainingCountries = countries.slice(maxVisible)
                  const hasMore = remainingCountries.length > 0

                  return (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {visibleCountries.map((countryCode, index) => (
                      <Tooltip key={index} title={getCountryName(countryCode, languageGSD)} arrow placement="top">
                        <span>
                          <Flag code={countryCode} style={{ fontSize: '20px' }}/>
                        </span>
                      </Tooltip>
                    ))}
                    {hasMore && (
                      <Tooltip
                        title={
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxWidth: '300px' }}>
                            {remainingCountries.map((countryCode, index) => (
                              <span key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                <Flag code={countryCode} style={{ fontSize: '16px' }}/>
                                <span style={{ fontSize: '12px' }}>{getCountryName(countryCode, languageGSD)}</span>
                              </span>
                            ))}
                          </div>
                        }
                        arrow
                        placement="top"
                      >
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: 'rgba(0, 0, 0, 0.6)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)'
                          }
                        }}>
                          +{remainingCountries.length}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  )
                }
              }),
              columnHelper.accessor('export.countries', {
                header: t('mailTemplate.wizard.firmList.exportCountries'),
                cell: ({ row }) => {
                  const countries = row.original.export?.countries || []
                  const maxVisible = 7

                  if (countries.length === 0) {
                    return <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
                  }

                  const visibleCountries = countries.slice(0, maxVisible)
                  const remainingCountries = countries.slice(maxVisible)
                  const hasMore = remainingCountries.length > 0

                  return (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {visibleCountries.map((countryCode, index) => (
                      <Tooltip key={index} title={getCountryName(countryCode, languageGSD)} arrow placement="top">
                        <span>
                          <Flag code={countryCode} style={{ fontSize: '20px' }}/>
                        </span>
                      </Tooltip>
                    ))}
                    {hasMore && (
                      <Tooltip
                        title={
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxWidth: '300px' }}>
                            {remainingCountries.map((countryCode, index) => (
                              <span key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                <Flag code={countryCode} style={{ fontSize: '16px' }}/>
                                <span style={{ fontSize: '12px' }}>{getCountryName(countryCode, languageGSD)}</span>
                              </span>
                            ))}
                          </div>
                        }
                        arrow
                        placement="top"
                      >
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: 'rgba(0, 0, 0, 0.6)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)'
                          }
                        }}>
                          +{remainingCountries.length}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  )
                }
              })
            ]
          : []),
        columnHelper.accessor('companySectors', {
          header: t('mailTemplate.wizard.firmList.sector'),
          cell: ({ row }) => {
            const sectorsArr = row.original.companySectors

            const displayText = `-${sectorsArr?.[0]?.code}-${sectorsArr?.[0]?.name?.[languageGSD]} ${sectorsArr?.length >
            1
              ? '...'
              : ''}`
            const tooltipText = sectorsArr?.length > 1
              ? sectorsArr.map(
                (sector) => `-${sector.code}-${sector?.name?.[languageGSD] ||
                ''}`).join('\n')
              : null

            return (
              <div className="flex items-center gap-4">
                <Typography color="text.primary" className="font-medium">
                  <Tooltip
                    title={tooltipText && <div
                      style={{ whiteSpace: 'pre-line' }}>{tooltipText}</div>}
                    arrow
                  >
                    <span>
                      {displayText}
                    </span>
                  </Tooltip>
                </Typography>
              </div>
            )
          }
        }),
        columnHelper.accessor('companyAddress', {
          header: t('mailTemplate.wizard.firmList.companyAddress'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.companyAddress}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('city', {
          header: t('mailTemplate.wizard.firmList.city'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.city}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('companyType', {
          header: t('mailTemplate.wizard.firmList.companyType'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.companyType}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('incorporationDate', {
          header: t('mailTemplate.wizard.firmList.incorporationDate'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.incorporationDate}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('zipCode', {
          header: t('mailTemplate.wizard.firmList.zipCode'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.zipCode}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('phoneNumber', {
          header: t('mailTemplate.wizard.firmList.phone'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.phoneNumber}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('companyProfile', {
          header: t('mailTemplate.wizard.firmList.profile'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Typography color="text.primary" className="font-medium">
                  {row.original.companyProfile}
                </Typography>
              </div>
            </div>
          )
        })
      ]

      return columnsArr
    },
    [t, languageGSD, isRowSelected, handleRowToggle, firmKey, disableInteraction]
  )

  const table = useReactTable({
    data,
    rowCount,
    columns,
    state: {
      serverSidePagination
    },
    manualPagination: true,
    enableRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    autoResetPageIndex: false,
    getRowId: row => row.id
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', height: '100%' }}>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {fetching && <LoadingOverlay/>}
        <div className="overflow-x-auto" style={{ flex: 1 }}>
          <table className={className ?? tableStyles.table}>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header,
                        header.getContext())
                    }
                  </th>
                ))}
              </tr>
            ))}
            </thead>
            {table.getFilteredRowModel().rows.length === 0
              ? (
                <tbody>
                <tr>
                  <td colSpan={table.getVisibleFlatColumns().length}
                      className="text-center">
                    {!fetching && t('mailTemplate.wizard.firmList.noData')}
                  </td>
                </tr>
                </tbody>
                )
              : (
                <tbody>
                {table.getRowModel().rows.map(row => {
                  return (
                    <tr
                      key={row.id}
                      className={classnames({ selected: isRowSelected(firmKey, row.original) })}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell,
                          cell.getContext())}</td>
                      ))}
                    </tr>
                  )
                })}
                </tbody>
                )
            }
          </table>
        </div>

        <div style={{ flexShrink: 0, marginTop: 'auto' }}>
          <TablePaginationComponentServerSide
            infoText1={t('mailTemplate.wizard.firmList.tableInfo_1')}
            infoText2={t('mailTemplate.wizard.firmList.tableInfo_2')}
            count={rowCount}
            pageIndex={serverSidePagination?.pageIndex || 0}
            pageSize={serverSidePagination?.pageSize || 10}
            setPageIndex={(val) => {
              setServerSidePaginationByKey({
                pageSize: serverSidePagination?.pageSize || 10,
                pageIndex: val
              })
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default FirmListServerSide
